"use client";

import { AlertCircle, RefreshCcw, X } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { signInAgain } from "@/lib/auth-recovery";
import type { ErrorRecovery } from "@/lib/handle-error";
import { openReportIssueDialog } from "@/lib/feedback/report-issue-event";
import type { ReportIssueContext } from "@/lib/feedback/types";

interface ActionErrorSource {
  error?: string | null;
  clearError?: () => void;
  recovery?: ErrorRecovery;
  retry?: () => void;
  reportable?: boolean;
  errorEventId?: string;
}

export interface ActionErrorNextStep {
  label: string;
  onClick: () => void;
}

interface ActionErrorAlertProps {
  error?: string | null;
  onDismiss?: () => void;
  onRetry?: () => void;
  nextStep?: ActionErrorNextStep | null;
  recovery?: ErrorRecovery;
  className?: string;
  from?: ActionErrorSource;
  reportable?: boolean;
  errorEventId?: string;
  reportContext?: Omit<ReportIssueContext, "errorMessage" | "errorEventId"> & {
    errorMessage?: string;
  };
}

function RecoveryAction({
  recovery,
  onRetry,
}: {
  recovery: ErrorRecovery;
  onRetry?: () => void;
}) {
  if (onRetry) {
    return (
      <Button type="button" variant="outline" size="compact" onClick={onRetry}>
        <RefreshCcw className="size-3.5" />
        Try again
      </Button>
    );
  }
  if (recovery === "sign-in") {
    return (
      <Button type="button" variant="outline" size="compact" onClick={signInAgain}>
        Sign in
      </Button>
    );
  }
  return null;
}

function ReportProblemButton({
  error,
  errorEventId,
  reportContext,
}: Pick<ActionErrorAlertProps, "errorEventId" | "reportContext"> & {
  error: string;
}) {
  return (
    <Button
      type="button"
      variant="link"
      size="bare-xs"
      onClick={() => {
        openReportIssueDialog({
          errorMessage: reportContext?.errorMessage ?? error,
          errorDigest: reportContext?.errorDigest,
          issueRef: reportContext?.issueRef,
          pageUrl: reportContext?.pageUrl,
          titlePrefix: reportContext?.titlePrefix,
          posthogReplayUrl: reportContext?.posthogReplayUrl,
          errorEventId,
        });
      }}
    >
      Report problem
    </Button>
  );
}

interface ResolvedErrorSource extends ActionErrorSource {
  recovery: ErrorRecovery;
  reportable: boolean;
}

function resolveSource(props: ActionErrorAlertProps): ResolvedErrorSource {
  const { from } = props;
  if (from) {
    return {
      ...from,
      recovery: from.recovery ?? "none",
      reportable: from.reportable ?? false,
      retry: props.onRetry ?? from.retry,
      clearError: from.clearError ?? props.onDismiss,
    };
  }
  return {
    error: props.error ?? null,
    clearError: props.onDismiss,
    recovery: props.recovery ?? (props.onRetry ? "retry" : "none"),
    retry: props.onRetry,
    reportable: props.reportable ?? false,
    errorEventId: props.errorEventId,
  };
}

function ErrorActions({
  nextStep,
  recovery,
  retry,
  error,
  reportable,
  errorEventId,
  reportContext,
}: Pick<ActionErrorAlertProps, "nextStep" | "errorEventId" | "reportContext"> & {
  recovery: ErrorRecovery;
  retry: (() => void) | undefined;
  error: string;
  reportable: boolean;
}) {
  if (!nextStep && !retry && recovery !== "sign-in" && !reportable) return null;
  return (
    <div className="flex shrink-0 items-center gap-1">
      {nextStep ? (
        <Button type="button" variant="primary" size="compact" onClick={nextStep.onClick}>
          {nextStep.label}
        </Button>
      ) : null}
      <RecoveryAction recovery={recovery} onRetry={retry} />
      {reportable && <ReportProblemButton error={error} errorEventId={errorEventId} reportContext={reportContext} />}
    </div>
  );
}

export function ActionErrorAlert(props: ActionErrorAlertProps) {
  const { error, clearError, recovery, retry, reportable, errorEventId } =
    resolveSource(props);

  if (!error) return null;

  return (
    <div
      role="alert"
      aria-live="assertive"
      className={cn(
        "animate-in fade-in-0 slide-in-from-top-1 flex w-full items-start gap-2.5 rounded-sm border border-destructive/25 bg-destructive-bg/40 py-2 pl-3 pr-2 text-sm text-foreground duration-150",
        props.className,
      )}
    >
      <AlertCircle
        aria-hidden="true"
        className="mt-0.5 size-4 shrink-0 text-destructive-strong"
      />
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1.5">
        <p className="min-w-0 flex-[1_1_14rem] py-px leading-snug">{error}</p>
        <ErrorActions
          nextStep={props.nextStep}
          recovery={recovery}
          retry={retry}
          error={error}
          reportable={reportable}
          errorEventId={errorEventId}
          reportContext={props.reportContext}
        />
      </div>
      {clearError && (
        <Button
          type="button"
          variant="quiet"
          size="icon-compact"
          onClick={clearError}
          aria-label="Dismiss error"
          className="-my-0.5 shrink-0"
        >
          <X />
        </Button>
      )}
    </div>
  );
}
